import React, { useId } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export interface LineChartDataType {
  label: string;
  [key: string]: string | number;
}

interface CustomLineChartProperties {
  data: LineChartDataType[];
  height?: number | string;
  series: {
    key: string;
    name: string;
    color: string;
    gradientColors: [string, string];
  }[];
}

/**
 * CustomLineChart - A shared area/line chart component with premium gradients.
 * @param {any} param0 - The param0
 * @param {any} param0.data - The data prop
 * @param {any} param0.height - The height prop
 * @param {any} param0.series - The series prop
 */
function CustomLineChart({ data, height = 300, series }: CustomLineChartProperties) {
  const chartId = useId().replace(/:/g, ''); // React 18 useId, remove colons for valid HTML IDs

  return (
    <div className="custom-line-chart-wrap" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 10,
            right: 10,
            left: -20,
            bottom: 0,
          }}>
          <defs>
            {series.map((s, index) => {
              const safeKey = s.key.replace(/\s+/g, '-');
              return (
                <linearGradient
                  key={`gradient-${index}`}
                  id={`color-${safeKey}-${chartId}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop offset="5%" stopColor={s.gradientColors[0]} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={s.gradientColors[1]} stopOpacity={0} />
                </linearGradient>
              );
            })}
          </defs>
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#64748B' }}
            interval="preserveStartEnd"
          />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748B' }} />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 4px 12px #64748B',
              fontSize: '12px',
            }}
          />
          {series.map((s, index) => {
            const safeKey = s.key.replace(/\s+/g, '-');
            return (
              <Area
                key={`area-${index}`}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#color-${safeKey}-${chartId})`}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

CustomLineChart.defaultProps = {
  height: 300,
};

export default CustomLineChart;
